import React, { useState, useEffect } from 'react';
import { Stack, Typography, Card, TextField, Button, Grid, IconButton, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import api from '../Services/Api';
import { useTranslation } from 'react-i18next';
import { glassCard, TEXT_PRIMARY, TEXT_SECONDARY } from '../designsystem/tokens';

function getAudioStatus(item) {
    if (!item.text || item.text.trim() === '') return 'empty';
    return 'draft';
}

function AudioStatusBadge({ item, t }) {
    const status = getAudioStatus(item);
    if (status === 'empty') {
        return (
            <Chip
                icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                label={t('audioStatusEmpty')}
                size="small"
                sx={{ bgcolor: 'rgba(211,47,47,0.15)', color: '#ef9a9a', border: '1px solid rgba(211,47,47,0.3)', height: 24, fontSize: '0.7rem' }}
            />
        );
    }
    return (
        <Chip
            icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
            label={t('audioStatusDraft')}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.1)', height: 24, fontSize: '0.7rem' }}
        />
    );
}

const AudioHistory = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [audioHistory, setAudioHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editedText, setEditedText] = useState('');

    useEffect(() => {
        fetchAudioHistory();
    }, []);

    const fetchAudioHistory = async () => {
        try {
            const response = await api.getAudioHistory();
            setAudioHistory(response || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching audio history:', error);
            setLoading(false);
        }
    };

    const handleEdit = (id, text) => {
        setEditingId(id);
        setEditedText(text);
    };

    const handleSave = async (id) => {
        try {
            await api.updateAudioText(id, editedText);
            setAudioHistory(audioHistory.map(item =>
                item._id === id ? { ...item, text: editedText } : item
            ));
            setEditingId(null);
        } catch (error) {
            console.error('Error updating audio text:', error);
        }
    };

    const handleResend = async (id, text) => {
        navigate('/app/validate', { state: { text } });
    };

    return (
        <Stack sx={{ padding: '1rem', maxWidth: '100%', margin: 'auto' }}>
            <Stack direction="row" spacing={2} sx={{ marginBottom: '1rem' }} alignItems="center">
                <IconButton sx={{ color: TEXT_PRIMARY, minWidth: 44, minHeight: 44 }} onClick={() => navigate('/app')} aria-label="back" data-testid="audio-back">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ color: TEXT_PRIMARY }}>{t('audioHistory')}</Typography>
            </Stack>

            {loading ? (
                <Typography sx={{ color: TEXT_PRIMARY }}>{t('loading')}</Typography>
            ) : audioHistory.length === 0 ? (
                <Typography sx={{ color: TEXT_SECONDARY }}>{t('noAudioHistory')}</Typography>
            ) : (
                <Grid container spacing={2}>
                    {audioHistory.map((item, idx) => (
                        <Grid item xs={12} key={item._id}>
                            <Card
                                sx={{
                                    ...glassCard,
                                    marginBottom: '1rem',
                                    borderLeft: `3px solid ${getAudioStatus(item) === 'empty' ? 'rgba(211,47,47,0.5)' : 'rgba(255,255,255,0.1)'}`,
                                }}
                                data-testid={`audio-card-${idx}`}
                            >
                                <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle2" sx={{ color: TEXT_SECONDARY }}>
                                                {new Date(item.createdAt).toLocaleString()}
                                            </Typography>
                                            <AudioStatusBadge item={item} t={t} />
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            {editingId === item._id ? (
                                                <>
                                                    <Button
                                                        size="small"
                                                        startIcon={<CheckIcon />}
                                                        onClick={() => handleSave(item._id)}
                                                        sx={{ color: TEXT_PRIMARY, minHeight: 44 }}
                                                        data-testid="audio-save"
                                                        aria-label={t('save')}
                                                    >
                                                        {t('save')}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        startIcon={<CloseIcon />}
                                                        onClick={() => setEditingId(null)}
                                                        sx={{ color: TEXT_PRIMARY, minHeight: 44 }}
                                                        data-testid="audio-cancel"
                                                        aria-label={t('cancel')}
                                                    >
                                                        {t('cancel')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="small"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => handleEdit(item._id, item.text)}
                                                        sx={{ color: TEXT_PRIMARY, minHeight: 44 }}
                                                        data-testid="audio-edit"
                                                        aria-label={t('edit')}
                                                    >
                                                        {t('edit')}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        startIcon={<SendIcon />}
                                                        onClick={() => handleResend(item._id, item.text)}
                                                        sx={{ color: TEXT_PRIMARY, minHeight: 44 }}
                                                        data-testid="audio-send"
                                                        aria-label={t('resend')}
                                                    >
                                                        {t('resend')}
                                                    </Button>
                                                </>
                                            )}
                                        </Stack>
                                    </Stack>
                                    {editingId === item._id ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            value={editedText}
                                            onChange={(e) => setEditedText(e.target.value)}
                                            variant="outlined"
                                            data-testid="audio-text-field"
                                        />
                                    ) : (
                                        <Typography sx={{ color: TEXT_PRIMARY }}>{item.text}</Typography>
                                    )}
                                </Stack>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Stack>
    );
};

export default AudioHistory;
